/**
 * WebView bridge (Port / Adapter).
 *
 * The native shell that hosts this web app injects a `window._PuerInterface`
 * object. Every native capability (ads, notifications, analytics, reel mode)
 * is exposed as a method on it. The rest of the app must never touch that
 * global directly - it talks to these pure adapter functions instead, so the
 * web app stays testable and degrades gracefully in a normal browser.
 */

/** @returns {Record<string, unknown> | undefined} */
export function getInterface() {
  return typeof window !== 'undefined' ? window._PuerInterface : undefined;
}

/**
 * Resolve a bound native method by name, or null when unavailable.
 * @param {string} name
 * @returns {Function | null}
 */
export function getMethod(name) {
  const iface = getInterface();
  if (iface && typeof iface[name] === 'function') {
    return iface[name].bind(iface);
  }
  return null;
}

/**
 * Call a native getter and fall back to a default when the bridge is absent
 * or throws. Used for capability checks like `isAppOpenAvailable`.
 * @template T
 * @param {string} name
 * @param {T} fallback
 * @returns {T}
 */
export function callGetter(name, fallback) {
  const fn = getMethod(name);
  if (!fn) return fallback;
  try {
    return fn();
  } catch {
    return fallback;
  }
}

/** True when running inside the native shell. */
export function hasBridge() {
  return !!getInterface();
}

/* ------------------------------------------------------------------ *
 * Capability checks
 * ------------------------------------------------------------------ */

export const canRequestNotifications = () =>
  !!getMethod('requestNotificationPermission');
export const isAppOpenAvailable = () => callGetter('isAppOpenAvailable', false);
export const isInterstitialAvailable = () =>
  callGetter('isInterstitialAvailable', false);
export const isNotificationGranted = () =>
  callGetter('notificationPermissionGranted', false);

/* ------------------------------------------------------------------ *
 * Imperative native actions
 * ------------------------------------------------------------------ */

/** Put the shell into vertical "reel" mode (immersive, no chrome). */
export function setReelMode(enabled) {
  const fn = getMethod('setIsReelMode');
  if (fn) {
    try {
      fn(enabled);
    } catch {
      /* noop */
    }
  }
}

/** Heartbeat that keeps the native ads overlay cooldown timer alive. */
export function adsOverlayCooldown() {
  const fn = getMethod('adsOverlayCooldown');
  if (fn) {
    try {
      fn();
    } catch {
      /* noop */
    }
  }
}

/**
 * Run the rewarded-ad check + show flow if the shell has one ready.
 * Mirrors: `if (_PuerInterface?.isRewardedReady?.()) _PuerInterface.showRewarded()`.
 */
export function maybeShowRewarded() {
  const iface = getInterface();
  if (iface && typeof iface.isRewardedReady === 'function') {
    try {
      if (iface.isRewardedReady() && typeof iface.showRewarded === 'function') {
        iface.showRewarded();
        return true;
      }
    } catch {
      /* noop */
    }
  }
  return false;
}

/** Show a native interstitial ad. */
export function showInterstitial() {
  const fn = getMethod('showInterstitial');
  if (fn) {
    fn(true);
    return true;
  }
  return false;
}

/**
 * Ask the OS to set this app as the default browser.
 * @param {string} callbackExpr - global expression invoked by the shell.
 */
export function requestSetDefaultBrowser(callbackExpr) {
  const fn = getMethod('requestSetDefaultBrowser');
  if (fn) {
    try {
      fn(callbackExpr);
    } catch {
      /* noop */
    }
  }
}

/**
 * Ask the OS for notification permission.
 * @param {string} [callbackExpr]
 */
export function requestNotificationPermission(callbackExpr) {
  const fn = getMethod('requestNotificationPermission');
  if (fn) {
    try {
      callbackExpr ? fn(callbackExpr) : fn();
    } catch {
      /* noop */
    }
  }
}
